import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, XCircle, AlertTriangle, Users } from 'lucide-react';

interface CheckResult {
  name?: string;
  exists: boolean;
}

interface BatchResult {
  index: number;
  name: string;
  exists: boolean;
}

export default function HeadhunterPage() {
  const token = window.location.pathname.split('/').filter(Boolean)[1] || '';
  const [info, setInfo] = useState<any>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Single check
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [checkError, setCheckError] = useState('');

  // Batch check
  const [batchInput, setBatchInput] = useState('');
  const [batchResults, setBatchResults] = useState<BatchResult[] | null>(null);
  const [batchChecking, setBatchChecking] = useState(false);

  const [mode, setMode] = useState<'single' | 'batch'>('single');

  useEffect(() => {
    fetch(`/api/headhunter/${token}`)
      .then(async res => {
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || '链接无效');
        }
        return res.json();
      })
      .then(data => { setInfo(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [token]);

  const handleCheck = async () => {
    if (!phone.trim() && !name.trim() && !email.trim()) return;
    setChecking(true);
    setResult(null);
    setCheckError('');
    try {
      const res = await fetch(`/api/headhunter/${token}/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), name: name.trim(), email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setResult(data);
      if (info) setInfo({ ...info, remaining: data.remaining });
    } catch (err: any) {
      setCheckError(err.message || '查询失败');
    } finally {
      setChecking(false);
    }
  };

  const handleBatchCheck = async () => {
    const lines = batchInput.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return;

    // Parse: each line is "name phone" or "name email" or just "phone"
    const candidates = lines.map(line => {
      const parts = line.trim().split(/[\t,，\s]+/);
      const entry: any = {};
      for (const p of parts) {
        if (/^1[3-9]\d{9}$/.test(p)) entry.phone = p;
        else if (p.includes('@')) entry.email = p;
        else if (!entry.name) entry.name = p;
      }
      return entry;
    });

    setBatchChecking(true);
    setBatchResults(null);
    setCheckError('');
    try {
      const res = await fetch(`/api/headhunter/${token}/check-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidates }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setBatchResults(data.results);
      if (info) setInfo({ ...info, remaining: data.remaining });
    } catch (err: any) {
      setCheckError(err.message || '查询失败');
    } finally {
      setBatchChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertTriangle size={48} className="text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-lg mx-auto pt-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Users size={22} className="text-indigo-600" />
                候选人查重
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">{info?.name}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">今日剩余</p>
              <p className="text-xl font-bold text-indigo-600">{info?.remaining ?? '-'}</p>
            </div>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => setMode('single')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'single' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
            单人查重
          </button>
          <button onClick={() => setMode('batch')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'batch' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
            批量查重
          </button>
        </div>

        {/* Single check */}
        {mode === 'single' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <p className="text-xs text-gray-500 mb-4">输入候选人信息查询是否已在系统中（手机号最准确）</p>
            <div className="space-y-3">
              <input value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="手机号（推荐）" maxLength={11}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none" />
              <input value={email} onChange={e => setEmail(e.target.value)}
                placeholder="邮箱（可选）"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none" />
              <input value={name} onChange={e => setName(e.target.value)}
                placeholder="姓名（仅在无手机号/邮箱时使用）"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none" />
              <button onClick={handleCheck} disabled={checking || (!phone.trim() && !name.trim() && !email.trim())}
                className="w-full py-2.5 bg-indigo-600 text-white rounded-lg font-medium text-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2">
                <Search size={16} /> {checking ? '查询中...' : '查重'}
              </button>
            </div>

            {checkError && <p className="text-red-500 text-sm mt-3">{checkError}</p>}
            {result && (
              <div className={`mt-4 p-4 rounded-xl flex items-center gap-3 ${result.exists ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                {result.exists ? (
                  <>
                    <XCircle size={24} className="text-red-500" />
                    <div>
                      <p className="font-medium text-red-700">已存在</p>
                      <p className="text-xs text-red-500">该候选人已在系统中，请勿重复推荐</p>
                    </div>
                  </>
                ) : (
                  <>
                    <CheckCircle size={24} className="text-green-500" />
                    <div>
                      <p className="font-medium text-green-700">未收录</p>
                      <p className="text-xs text-green-500">系统中暂无该候选人记录，可以推荐</p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Batch check */}
        {mode === 'batch' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <p className="text-xs text-gray-500 mb-2">每行一个候选人，格式：姓名 手机号（或邮箱），最多50人</p>
            <textarea value={batchInput} onChange={e => setBatchInput(e.target.value)}
              placeholder={"张三 13800138000\n李四 lisi@example.com\n王五 13900139000"}
              rows={8}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none" />
            <button onClick={handleBatchCheck} disabled={batchChecking || !batchInput.trim()}
              className="w-full mt-3 py-2.5 bg-indigo-600 text-white rounded-lg font-medium text-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2">
              <Search size={16} /> {batchChecking ? '查询中...' : '批量查重'}
            </button>

            {checkError && <p className="text-red-500 text-sm mt-3">{checkError}</p>}
            {batchResults && (
              <div className="mt-4 space-y-2">
                <p className="text-xs text-gray-500">
                  共 {batchResults.length} 人，
                  <span className="text-red-600 font-medium">{batchResults.filter(r => r.exists).length} 人已存在</span>，
                  <span className="text-green-600 font-medium">{batchResults.filter(r => !r.exists).length} 人未收录</span>
                </p>
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {batchResults.map((r, i) => (
                    <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${r.exists ? 'bg-red-50' : 'bg-green-50'}`}>
                      <span className="text-gray-700">{r.name || `#${r.index + 1}`}</span>
                      {r.exists ? (
                        <span className="text-red-600 text-xs font-medium flex items-center gap-1"><XCircle size={14} /> 已存在</span>
                      ) : (
                        <span className="text-green-600 text-xs font-medium flex items-center gap-1"><CheckCircle size={14} /> 未收录</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">仅用于查询候选人是否已在系统中，不返回任何详细信息</p>
      </div>
    </div>
  );
}
